import { API, ApiEndpoint } from '@/models/api';
import { BaseResponse } from '@/models/baseResponse';
import { UserDto, UpdateUserDto, User, UserSettings, UserStatus } from '@/models/user';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  combineLatest,
  EMPTY,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ManagementKey } from 'src/app/admin/components/api-key-management/api-key-management.model';
import { Company } from 'src/app/admin/components/company-management/company-management.model';
import {
  CompletionRuleDto,
  CompletionRulesEnvelopeDto,
} from 'src/app/admin/components/module-access-configuration/completion-rules/completion-rules.component';
import { Tenant } from 'src/app/admin/components/tenant-management/tenant-management.model';
import { RegistrationRequest } from 'src/app/public/components/registration/registration-requests.model';
import {
  buildEffectiveProfileList,
  collectModuleProfileHierarchy,
} from 'src/app/shared/swp-components/swp-table/profile-source-utils';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private http = inject(HttpClient);
  private userSettingsCache$: Observable<any> | null = null;
  private userProfilesCache$: Observable<any> | null = null;
  private companiesCache$: Observable<any[]> | null = null;
  private activeCompaniesCache$: Observable<any[]> | null = null;
  private managementKeysCache$: Observable<any[]> | null = null;
  private companyCache = new Map<string, Observable<any>>();
  private auditsCache = new Map<string, Observable<any[]>>();
  private tenantsCache$: Observable<Tenant[]> | null = null;
  private activeTenantsCache$: Observable<Tenant[]> | null = null;
  private modulesCache$: Observable<any[]> | null = null;
  private activeTenantsCache = new Map<string, Observable<Tenant[]>>();
  private tenantsForCompanyCache$: Observable<Tenant[]> | null = null;
  private companiesForApiKeyCache$: Observable<any[]> | null = null;
  private modulesCaches = new Map<'company' | 'tenant' | 'user', Observable<any[]>>();

  deleteTenantsCache() {
    this.tenantsCache$ = null;
    this.activeTenantsCache.clear();
    this.tenantsForCompanyCache$ = null;
  }

  deleteCompaniesCache() {
    this.companiesCache$ = null;
    this.activeCompaniesCache$ = null;
    this.companyCache.clear();
    this.companiesForApiKeyCache$ = null;
  }

  deleteManagementKeysCache() {
    this.managementKeysCache$ = null;
  }

  clearUserSettingsCache() {
    this.userSettingsCache$ = null;
  }

  clearUserProfilesCache() {
    this.userProfilesCache$ = null;
  }

  getRegistrationRequestById(id?: string): Observable<RegistrationRequest> {
    if (!id) {
      return EMPTY;
    }
    return this.http.get<BaseResponse>(API.Admin.RegistrationsSingle(id).url).pipe(
      map((res) => {
        if (!res.success) return null;
        return res.data.item;
      }),
    );
  }

  getAllRegistrations(): Observable<RegistrationRequest[]> {
    return this.http.get<BaseResponse>(API.Admin.GetAllRegistrations.url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.items;
      }),
    );
  }

  getCompanies(active = false) {
    const cacheKey = active ? 'activeCompaniesCache$' : 'companiesCache$';
    const url = (active ? API.Admin.GetActiveCompanies : API.Admin.GetAllCompanies).url;

    if (!this[cacheKey]) {
      this[cacheKey] = this.http.get<BaseResponse>(url).pipe(
        map((res) => (res.success ? res.data.items : [])),
        map((items: any[]) => items.map((c: any) => ({ ...c, code: c.id, name: c.name }))),
        shareReplay(1),
      );
    }

    return this[cacheKey]!;
  }

  getAllCompaniesForApiKey() {
    if (!this.companiesForApiKeyCache$) {
      this.companiesForApiKeyCache$ = this.http
        .get<BaseResponse>(API.Admin.GetAllCompaniesForApiKey.url)
        .pipe(
          map((res) => (res.success ? res.data.items : [])),
          map((items: any[]) => items.map((c: any) => ({ ...c, code: c.id, name: c.name }))),
          shareReplay(1),
        );
    }
    return this.companiesForApiKeyCache$;
  }

  getAllUsers() {
    return this.http.get<BaseResponse>(API.Admin.GetAllUsers.url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.items;
      }),
      map((items: any[]) => items.map((c: any) => ({ ...c, code: c.id, name: c.name }))),
    );
  }

  approveRegistrationRequest(
    requestId: string,
    companyIds: string[],
    sendActivationEmail: boolean,
  ): Observable<any> {
    return this.http.patch(API.Admin.ApproveRegistration(requestId).url, {
      companyIds,
      sendActivationEmail,
    });
  }

  rejectRegistrationRequest(requestId: string, rejectComment: string): Observable<any> {
    return this.http.patch(
      API.Admin.RejectRegistration(requestId).url,
      JSON.stringify(rejectComment),
    );
  }

  getTenants(active = false, userId?: string): Observable<Tenant[]> {
    if (!active) {
      const url = API.Admin.GetAllTenants.url;
      if (!this.tenantsCache$) {
        this.tenantsCache$ = this.http.get<BaseResponse>(url).pipe(
          map((res) => (res.success ? res.data.items : [])),
          shareReplay(1),
        );
      }
      return this.tenantsCache$;
    }

    const cacheKey = userId ?? '';
    if (!this.activeTenantsCache.has(cacheKey)) {
      const params = userId ? new HttpParams().set('userId', userId) : undefined;
      this.activeTenantsCache.set(
        cacheKey,
        this.http.get<BaseResponse>(API.Admin.GetActiveTenants.url, { params }).pipe(
          map((res) => (res.success ? res.data.items : [])),
          shareReplay(1),
        ),
      );
    }

    return this.activeTenantsCache.get(cacheKey)!;
  }

  getAllTenantsForCompany(): Observable<Tenant[]> {
    if (!this.tenantsForCompanyCache$) {
      this.tenantsForCompanyCache$ = this.http
        .get<BaseResponse>(API.Admin.GetAllTenantsForCompany.url)
        .pipe(
          map((res) => (res.success ? res.data.items : [])),
          shareReplay(1),
        );
    }
    return this.tenantsForCompanyCache$;
  }

  getTenantDetailsForUser(id: string) {
    return this.http.get<BaseResponse>(API.Admin.GetTenantDetailsForUser(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  getRoles(): Observable<any[]> {
    return this.http
      .get<BaseResponse>(API.Admin.GetAllRolesForUser.url)
      .pipe(map((res) => (res.success ? res.data.items : [])));
  }

  getModules(source: 'company' | 'tenant' | 'user' = 'company'): Observable<any[]> {
    const endpoint =
      source === 'tenant'
        ? API.Admin.GetAllTenantModules
        : source === 'user'
          ? API.Admin.GetAllUserModules
          : API.Admin.GetAllCompanyModules;
    const cachedModules = this.modulesCaches.get(source);

    if (!cachedModules) {
      const modules = this.http.get<BaseResponse>(endpoint.url).pipe(
        map((res) => {
          if (!res.success) return [];
          return res.data.items;
        }),
        shareReplay(1),
      );
      this.modulesCaches.set(source, modules);
      return modules;
    }

    return cachedModules;
  }

  getAllManagementKeys(): Observable<any[]> {
    if (!this.managementKeysCache$) {
      this.managementKeysCache$ = this.http
        .get<BaseResponse>(API.Admin.GetAllManagementKeys.url)
        .pipe(
          map((res) => {
            if (!res.success) return [];
            return res.data.items;
          }),
          shareReplay(1),
        );
    }
    return this.managementKeysCache$;
  }

  getAllTypeMappingItems(hasMapping?: boolean): Observable<any[]> {
    return this.http.get<BaseResponse>(API.Admin.GetAllTypeMappingItems(hasMapping).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.items;
      }),
    );
  }

  getAllManagementKeysWithCompanyName(): Observable<any[]> {
    return combineLatest([this.getAllManagementKeys(), this.getCompanies()]).pipe(
      map(([keys, companies]) => {
        const companyMap = new Map(companies.map((c) => [c.id, c.name]));

        return keys.map((k) => ({
          ...k,
          companyName: companyMap.get(k.companyId) ?? null,
        }));
      }),
    );
  }

  getAllCompaniesByTenant(id: string): Observable<Company[]> {
    if (!id) return of([]);
    return this.http.get<BaseResponse>(API.Admin.GetAllCompaniesByTenantId(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  getAllCompaniesByTenantForUser(id: string): Observable<Company[]> {
    if (!id) return of([]);
    return this.http.get<BaseResponse>(API.Admin.GetAllCompaniesByTenantForUser(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  getUserProfilesById(id: string): Observable<any> {
    return this.http
      .get<BaseResponse>(API.Admin.GetUserProfilesById(id).url)
      .pipe(map((res) => (res.success ? res.data.item : null)));
  }

  getCompanyProfiles(id: string): Observable<any> {
    return this.http
      .get<BaseResponse>(API.Admin.GetCompanyProfiles(id).url)
      .pipe(map((res) => (res.success ? res.data.item : null)));
  }

  getTenantProfiles(id: string): Observable<any> {
    return this.http
      .get<BaseResponse>(API.Admin.GetTenantProfiles(id).url)
      .pipe(map((res) => (res.success ? res.data.item : null)));
  }

  getUsersByCompanyID(id: string): Observable<User[]> {
    return this.http.get<BaseResponse>(API.Admin.GetAllUsersByCompanyId(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  getTenant(id: string) {
    return this.http.get<BaseResponse>(API.Admin.GetTenantDetails(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  getCompany(id: string) {
    if (!this.companyCache.has(id)) {
      this.companyCache.set(
        id,
        this.http.get<BaseResponse>(API.Admin.GetCompanyDetails(id).url).pipe(
          map((res) => {
            if (!res.success) return [];
            return res.data.item;
          }),
          shareReplay(1),
        ),
      );
    }
    return this.companyCache.get(id)!;
  }

  getUser(id: string) {
    return this.http.get<BaseResponse>(API.Admin.GetUserDetails(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return res.data.item;
      }),
    );
  }

  saveTenant(tenant: Tenant | null) {
    if (!tenant) return;
    return this.http.patch<BaseResponse>(
      (!tenant.id ? API.Admin.AddTenant : API.Admin.UpdateTenant).url,
      JSON.stringify(tenant),
    );
  }

  saveUserSettings(settings: UserSettings | null) {
    if (!settings) return;
    // Invalidate cache and refetch fresh data after successful save
    return this.http.patch<BaseResponse>(API.Admin.SaveUserSettings.url, settings).pipe(
      tap(() => {
        this.userSettingsCache$ = null;
      }),
      switchMap(() => this.getUserSettings()),
    );
  }

  saveCompany(company: Company | null) {
    if (!company) return;
    return this.http.patch<BaseResponse>(
      (!company.id ? API.Admin.AddCompany : API.Admin.UpdateCompany).url,
      JSON.stringify(company),
    );
  }

  saveUser(user: User | null) {
    if (!user) return;

    if (user.id) {
      // Update existing user - use UpdateUserDto (without email and username)
      const updateUserDto: UpdateUserDto = {
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        tenantId: user.tenantId ?? '',
        status:
          user.status && Object.values(UserStatus).includes(user.status as UserStatus)
            ? (user.status as UserStatus)
            : UserStatus.Active,
        profilesJson: user.profilesJson,
        roles: user.roles ?? [],
        companyIds: user.userCompanies?.map((uc) => uc.companyId).filter((id) => id) ?? [],
      };

      return this.http.patch<BaseResponse>(API.Admin.UpdateUser.url, updateUserDto);
    } else {
      // Create new user - use UserDto (with email and username)
      const createUserDto: UserDto = {
        id: user.id,
        userName: user.email,
        email: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        tenantId: user.tenantId ?? '',
        status:
          user.status && Object.values(UserStatus).includes(user.status as UserStatus)
            ? (user.status as UserStatus)
            : UserStatus.Active,
        profilesJson: user.profilesJson,
        roles: user.roles ?? [],
        companyIds: user.userCompanies?.map((uc) => uc.companyId).filter((id) => id) ?? [],
      };

      return this.http.patch<BaseResponse>(API.Admin.AddUser.url, createUserDto);
    }
  }

  updateUserStatus(userId: string | null, status: UserStatus) {
    return this.updateStatus(API.Admin.UpdateUserStatus.url, userId, status);
  }

  saveUserProfile(user: User | null) {
    if (!user?.id) return;
    return this.http.patch<BaseResponse>(API.Admin.UpdateUserProfile.url, {
      id: user.id,
      profilesJson: user.profilesJson,
    });
  }

  updateCompanyStatus(companyId: string | null, status: string) {
    return this.updateStatus(API.Admin.UpdateCompanyStatus.url, companyId, status);
  }

  updateTenantStatus(tenantId: string | null, status: string) {
    return this.updateStatus(API.Admin.UpdateTenantStatus.url, tenantId, status);
  }

  private updateStatus(url: string, id: string | null, status: string) {
    if (!id) return;

    return this.http.patch<BaseResponse>(url, { id, status });
  }

  saveManagementKey(mk: ManagementKey | undefined) {
    if (!mk) return;

    const payload: ManagementKey = {
      id: mk.id,
      companyId: mk.companyId,
      keyName: mk.keyName,
      clientID: mk.clientID,
      expDate: mk.expDate instanceof Date ? mk.expDate.toISOString() : (mk.expDate as any),
      notes: mk.notes,
    };

    return this.http.patch<BaseResponse>(
      (!mk.id ? API.Admin.AddMK : API.Admin.UpdateMK).url,
      JSON.stringify(payload),
    );
  }

  getManagementKeyDetails(id: string) {
    return this.http.get<BaseResponse>(API.Admin.GetManagementKeyDetails(id).url).pipe(
      map((res) => {
        if (!res.success) return [];
        return {
          ...res.data.item,
          expDate: res.data.item?.expDate ? new Date(res.data.item?.expDate) : undefined,
          secondaryExpDate: new Date(res.data.item?.secondaryExpDate),
        };
      }),
    );
  }

  saveTypeMappingItems(tmList: any[] | undefined) {
    if (!tmList) return;
    const headers = { 'Content-Type': 'application/json' };
    return this.http.patch<BaseResponse>(
      API.Admin.UpdateTypeMappingItems.url,
      JSON.stringify(tmList),
      {
        headers,
      },
    );
  }

  getAudits(auditEndpoint: ApiEndpoint, affectedEntityId?: string) {
    const entityId = affectedEntityId?.trim() ?? '';
    const cacheKey = `${auditEndpoint.url}:${entityId}`;
    if (!this.auditsCache.has(cacheKey)) {
      let params = new HttpParams();
      if (entityId) params = params.set('affectedEntityId', entityId);
      this.auditsCache.set(
        cacheKey,
        this.http.get<BaseResponse>(auditEndpoint.url, { params }).pipe(
          map((res) => (res.success ? res.data.audits : [])),
          map((audits: any[]) => audits.map((c: any) => ({ ...c, code: c.id, name: c.name }))),
          shareReplay(1),
        ),
      );
    }
    return this.auditsCache.get(cacheKey)!;
  }

  updateUserProfile(profileData: any) {
    // Get Original User Profile
    return this.http.get<BaseResponse>(API.Admin.GetCurrentUserDetails.url).pipe(
      tap((x: any) => {
        if (x.success) {
          let userDetails = x?.data.item;
          let allprofiles = userDetails?.profilesJson;
          if (!allprofiles) {
            allprofiles = '[]';
          }
          if (allprofiles) {
            allprofiles = JSON.parse(x?.data.item.profilesJson);
            if (!Array.isArray(allprofiles)) {
              allprofiles = [{ id: 'next360-logistics', enabled: true, profiles: [profileData] }];
            } else {
              let moduleProfile = allprofiles.find((x: any) => x.id == 'next360-logistics');
              if (!moduleProfile) {
                const { isDefault: _isDefault, ...persistedProfile } = profileData ?? {};
                allprofiles.push({
                  id: 'next360-logistics',
                  enabled: true,
                  defaultProfile: undefined,
                  profiles: [persistedProfile],
                });
              } else {
                let currProfiles = moduleProfile?.profiles;
                if (!currProfiles || !Array.isArray(currProfiles)) {
                  currProfiles = [];
                }
                let indexOf = currProfiles.findIndex((a: any) => a.name == profileData.name);
                if (indexOf != -1) {
                  currProfiles?.splice(indexOf, 1);
                }
                const { isDefault: _isDefault, ...persistedProfile } = profileData ?? {};
                currProfiles.push(persistedProfile);
                moduleProfile.profiles = currProfiles;
                allprofiles.splice(
                  allprofiles.findIndex((x: any) => x.id == 'next360-logistics'),
                  1,
                );
                allprofiles.push(moduleProfile);
              }
            }
          }

          userDetails.profilesJson = JSON.stringify(allprofiles);
          this.saveUserProfile(userDetails)?.subscribe();
        }
      }),
    );
  }
  getAllUserProfiles(forceRefresh = false): Observable<any> {
    if (forceRefresh) {
      this.userProfilesCache$ = null;
    }

    if (!this.userProfilesCache$) {
      this.userProfilesCache$ = this.http.get<BaseResponse>(API.Admin.GetAllUserProfiles.url).pipe(
        map((res: any) => {
          if (!res?.success) return [];

          const hierarchy = collectModuleProfileHierarchy('next360-logistics', {
            tenantProfilesJson: res?.data?.item?.['tenantsProfiles'],
            tenantFiltersJson: res?.data?.item?.['tenantFilters'],
            companyProfilesJson: res?.data?.item?.['companiesProfiles'],
            userProfilesJson: res?.data?.item?.['userProfiles'],
          });

          const allProfiles = buildEffectiveProfileList({
            hierarchy,
            localProfiles: hierarchy.profilesBySource.USER,
            defaultProfileName: hierarchy.defaultProfileNameBySource.USER,
          });

          const filterGroups: any[][] = [
            hierarchy.filtersBySource.TENANT,
            hierarchy.filtersBySource.COMPANY,
            hierarchy.filtersBySource.USER,
          ].filter((group) => group.length > 0);
          return { profiles: allProfiles, filters: filterGroups };
        }),
        shareReplay(1),
      );
    }
    return this.userProfilesCache$;
  }

  getUserSettings(): Observable<any> {
    if (!this.userSettingsCache$) {
      this.userSettingsCache$ = this.http
        .get<BaseResponse>(API.Admin.GetUserSettings.url)
        .pipe(shareReplay(1));
    }
    return this.userSettingsCache$;
  }

  CompletionRulesUpsertForCompany(
    companyId: string,
    moduleId: string,
    completionRules: CompletionRuleDto[],
    applyToPastShipments: boolean = false,
  ): Observable<CompletionRulesEnvelopeDto> {
    const sanitizedRules = completionRules.map((rule) => ({
      ...rule,
      conditions: rule.conditions.map((c) => ({
        ...c,
        value: c.value != null ? String(c.value) : c.value,
      })),
    }));
    const body: CompletionRulesEnvelopeDto = {
      companyId,
      moduleId,
      completionRules: sanitizedRules,
      applyToPastShipments,
    };
    return this.http.post<BaseResponse>(API.Admin.UpsertCompletionRules.url, body).pipe(
      map((res) => {
        if (!res.success) throw new Error(res.message || 'Unknown error');
        return res.data as CompletionRulesEnvelopeDto;
      }),
    );
  }

  CompletionRulesGetByCompany(companyId: string): Observable<CompletionRulesEnvelopeDto> {
    return this.http.get<BaseResponse>(API.Admin.GetCompletionRulesByCompany(companyId).url).pipe(
      map((res) => {
        if (!res.success) throw new Error(res.message || 'Unknown error');
        return res.data;
      }),
    );
  }

  CompletionRulesGetByModule(
    companyId?: string | null | undefined,
    moduleId?: string | null | undefined,
    columnFields: string[] = [],
  ): Observable<CompletionRulesEnvelopeDto> {
    if (!companyId) return of();
    if (!moduleId) return of();

    return this.http
      .get<BaseResponse>(API.Admin.GetCompletionRulesByModule(companyId, moduleId).url)
      .pipe(
        map((res) => {
          if (!res.success) throw new Error(res.message || 'Unknown error');
          const data = res.data as CompletionRulesEnvelopeDto;
          return {
            ...data,
            completionRules: this.normalizeCompletionRules(
              data?.completionRules ?? [],
              columnFields,
            ),
          } as CompletionRulesEnvelopeDto;
        }),
      );
  }

  // Normalization helpers for filters/thresholds/rules
  normalizeProfileFilters(filters: any, columnFields: string[]): any {
    const allowedFields = new Set(columnFields);
    const hasBaseFields = columnFields.length > 0;

    if (Array.isArray(filters)) {
      return filters.filter((entry: any) => {
        const key = entry?.fieldName;
        return !!key && (!hasBaseFields || allowedFields.has(key));
      });
    }

    if (filters && typeof filters === 'object') {
      const result: any = {};
      for (const [key, meta] of Object.entries(filters)) {
        if (hasBaseFields && !allowedFields.has(key)) continue;
        result[key] = meta;
      }
      return result;
    }

    return {};
  }

  normalizeProfileThresholds(thresholds: any[], columnFields: string[]): any[] {
    const allowedFields = new Set(columnFields);
    const hasBaseFields = columnFields.length > 0;

    if (!Array.isArray(thresholds)) return [];

    return thresholds
      .map((thresh: any) => ({
        ...thresh,
        conditions: Array.isArray(thresh?.conditions)
          ? thresh.conditions.filter(
              (cond: any) => !hasBaseFields || allowedFields.has(cond?.columnKey),
            )
          : [],
      }))
      .filter((thresh: any) => (thresh.conditions?.length ?? 0) > 0 || !hasBaseFields);
  }

  normalizeCompletionRules(rules: any[], columnFields: string[]): any[] {
    const allowedFields = new Set(columnFields);
    const hasBaseFields = columnFields.length > 0;

    if (!Array.isArray(rules)) return [];

    return rules
      .map((rule: any) => ({
        ...rule,
        conditions: Array.isArray(rule?.conditions)
          ? rule.conditions.filter(
              (cond: any) => !hasBaseFields || allowedFields.has(cond?.fieldName),
            )
          : [],
      }))
      .filter((rule: any) => (rule.conditions?.length ?? 0) > 0 || !hasBaseFields);
  }
}
